import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import posthog from 'posthog-js'
import { useSearchParams } from 'react-router-dom'
import { useStopwatch } from 'react-timer-hook'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import { useEnsAddress } from '@src/chains/evm/hooks/useEnsAddress'
import useSNSResolver from '@src/chains/solana/hooks/useSNSResolver'
import { isSNSDomain } from '@src/chains/solana/utils/sns'
import MilesTracker from '@src/components/MilesTracker'
import { TokenSparkline } from '@src/components/TokenSparkline'
import TransactionStatusCard from '@src/components/TransactionStatusCard'
import ConnectWallet from '@src/components/buttons/ConnectWallet'
import {
    ChainId,
    NATIVE_TOKEN_ADDRESS,
    NULLADDRESS,
    WRAPPED_GAS_TOKENS,
    getChainName,
    getChainNameFromChainId,
} from '@src/configs/chainInfo'
import { LARGE_SCREEN_THRESHOLD } from '@src/constants/exchange'
import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useFetchBoundReferralCode } from '@src/hooks/useFetchBoundReferralCode'
import { useFetchGeneratedReferralCode } from '@src/hooks/useFetchGeneratedReferralCode'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import AIInputBox from '@src/pages/Exchange/components/AIInputBox'
import { FromSection } from '@src/pages/Exchange/components/FromSection'
import { GasFeePanel } from '@src/pages/Exchange/components/GasFeePanel'
import { JupiterRouteModal } from '@src/pages/Exchange/components/JupiterRouteModal'
import { SwapActionButton } from '@src/pages/Exchange/components/SwapActionButton'
import SwapInProgressFlow from '@src/pages/Exchange/components/SwapInProgressFlow'
import { ToSection } from '@src/pages/Exchange/components/ToSection'
import { ToggleExchangeTokens } from '@src/pages/Exchange/components/ToggleExchangeTokens'
import { AllTooltips } from '@src/pages/Exchange/components/Tooltips'
import TransactionNotifications from '@src/pages/Exchange/components/TransactionNotifications'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import {
    useDestinationAddress,
    useExchangeAmountLogic,
    useExchangeDerivedState,
    useExchangeMemos,
    useExchangeToken,
    useOrderUpdates,
    useSwapContract,
    useToggleExchangeTokens,
} from '@src/pages/Exchange/hooks'
import { analyticsService } from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { FeatherQuoteError, IFeatherOrderCreateResponse, IFeatherOrderStatusResponse } from '@src/types/feather'
import { IQuoteData, ISandDollarsData } from '@src/types/order'
import { Address, IToken } from '@src/types/token'
import {
    createContextLogger,
    formatExchangeAmountWithCommas,
    formatNumberStringWithCommas,
    getAllowance,
    isFeatureEnabled,
    isUnWrapOnly,
    isWrapOnly,
    returnSanitizedSellAmount,
} from '@src/utils'
import { mapBackendToFrontendStatus } from '@src/utils/featherUtils'
import { createSandDollarsBalance } from '@src/utils/sandDollars'
import {
    handleTokenSelection,
    isSameExchangeToken,
    validateDefaultTokens,
    validateUrlTokenParams,
} from '@src/utils/tokenValidation'
import { useApplicationStore, useHistoryStore, useTokensStore } from '@src/zustand_stores'
import { useOrderUpdatesStore } from '@src/zustand_stores/orderUpdates.store'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

import FeatherFlow from './components/FeatherFlow'
import FeatherOrderStatusModal from './components/FeatherOrderFlow/FeatherOrderStatus'
import FeatherOrderNotice from './components/FeatherOrderNotice'
import useExchangeBalances from './hooks/useExchangeBalances'
import { useServerlessGasEstimation } from './hooks/useServerlessGasEstimation'

// Local implementation of ENS domain check
const isENSDomain = (name: string): boolean => {
    if (!name) return false
    return name.toLowerCase().includes('.')
}

const logger = createContextLogger('ExchangePage')

interface ExchangePageProps {
    hideAIInput?: boolean
}

const ExchangePage = ({ hideAIInput = false }: ExchangePageProps) => {
    const {
        isGasFeeExpanded,
        machFee,
        isReviewSwapModalOpen,
        isQuoteExpired,
        isManuallyRefreshing,
        isCustomRecipientWallet,
        hasInsufficientGas,
        isPriceFlipped,
        isToggleInProgress,
        sellAllowance,
        isQuoteEnabled,
        setIsGasFeeExpanded,
        setIsReviewSwapModalOpen,
        setIsCustomRecipientWallet,
        setIsPriceFlipped,
        setSellAllowance,
        setChainId,
        isBuyOutputLoading,
        setIsBuyOutputLoading,
    } = useExchangeState()

    const {
        isCrossChain,
        validateAddress,
        getAutoDetectedAddress,
        isCorrectWalletConnected,
        isWrapOrUnwrapOnly,
        isEVMConnected,
    } = useExchangeDerivedState()

    const {
        exchangeId,
        orderId,
        currentModalId,
        setExchangeId,
        setCurrentModalId,
        setWaitingForConnectedWalletInteraction,
        refreshStatus,
    } = useApplicationStore()

    const getOrder = useHistoryStore(state => state.getOrder)
    const updateHistory = useHistoryStore(state => state.updateHistory)
    const exchanges = useHistoryStore(state => state.history)
    const transactionData = getOrder(exchangeId)

    const [searchParams, setSearchParams] = useSearchParams()

    const isFeatherEnabled = isFeatureEnabled('ENABLE_FEATHER')

    /**
     * * * * * * * * * * * * * * * * * * * * *  NEW ZUSTAND STORES * * * * * * * * * * * * * * *
     */
    const { isTronConnected, evmWalletAddress, tronWalletAddress, isSolanaConnected, solanaWalletAddress, EVMChainId } =
        useWalletConnections()

    const tokensLoading = useTokensStore(state => state.isLoading)

    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const {
        sellData,
        buyData,
        quoteResponse,
        isQuoteLoading,
        refetchQuote,
        mainWalletAddress,
        updateSellData,
        updateBuyData,
        updateBothData,
        isFeatherOrder,
        liquidityAvailable,
        isCheckingLiquidity,
        liquidityError,
        featherQuote,
        featherQuoteError,
        isJupiterRouteModalOpen,
        closeJupiterRouteModal,
    } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    useFetchAllBalances() // This will automatically update token balances in the store

    useFetchBoundReferralCode() // This will automatically update bound referral code in application store

    useFetchGeneratedReferralCode() // This will automatically update generated referral code in application store

    const { nativeBalance, wrappedNativeBalance, refreshAllBalancesAndWait } = useExchangeBalances()

    // Add the invalidateBalances function
    const invalidateBalances = useInvalidateBalances()

    useEffect(() => {
        if (isTronConnected) {
            setChainId(ChainId.Tron)
        } else if (isSolanaConnected) {
            setChainId(ChainId.Solana)
        } else if (EVMChainId) {
            setChainId(EVMChainId)
        }
    }, [isTronConnected, isSolanaConnected, EVMChainId])

    // Websocket hook which runs in the background and only connects if there is a valid exchangeId + orderId
    useOrderUpdates(exchangeId, orderId)

    const { gasFeeString, isGasEstimationFetched, totalCostInGasToken } = useServerlessGasEstimation({
        sellData,
        buyData,
        sellAllowance,
        isWrapOrUnwrapOnly,
    })

    const {
        sellAmountString,
        isInsufficientBalance,
        isQuoteInvalid,
        liquiditySource,
        onChangeSellValue,
        handleManualRefresh,
        setMax,
        setSellAmountString,
    } = useExchangeAmountLogic({
        isGasEstimationFetched,
        totalCostInGasToken,
    })

    const { isCCTP, hasErrors, computedMilesAndMultiplier, feesDisplay } = useExchangeMemos({
        sellData,
        buyData,
        isInsufficientBalance,
        isQuoteInvalid,
        isBuyOutputLoading,
        isQuoteExpired,
        address: evmWalletAddress,
        solanaWalletAddress: solanaWalletAddress?.toString(),
        machFee,
    })

    const miles = computedMilesAndMultiplier.miles

    const { reset: resetStopwatch } = useStopwatch({ autoStart: false })

    const onCloseReviewSwapModal = () => {
        const isLargeScreen = window.innerWidth >= LARGE_SCREEN_THRESHOLD

        // Show a Toast if needed...
        if (
            exchanges[currentModalId] &&
            exchanges[currentModalId].result?.status !== ResultCode.success &&
            [StepNames.MATCHING, StepNames.MATCH_CREATED, StepNames.MATCH_EXECUTED].includes(
                exchanges[currentModalId].currentStep
            ) &&
            exchanges[currentModalId].result?.status === ResultCode.pending
        ) {
            // Create a unique toast ID
            const safeToastId = `modal-close-${currentModalId}-${Date.now()}`

            // Use TransactionStatusCard with isModalCloseNotification=true to avoid toggle errors
            toast(
                <TransactionStatusCard
                    exchange={exchanges[currentModalId]}
                    onClose={() => toast.dismiss(safeToastId)}
                    isSuccess={false}
                    isModalCloseNotification={true}
                />,
                {
                    autoClose: 5000,
                    position: isLargeScreen ? 'bottom-right' : 'bottom-center',
                    toastId: safeToastId,
                    pauseOnHover: true,
                    pauseOnFocusLoss: false,
                    onClose: () => {},
                }
            )
        }

        // If swap completed successfully, track it and reset amounts
        if (exchanges[currentModalId]?.result?.status === ResultCode.success) {
            updateSellData({ value: BigInt(0) })
            updateBuyData({ value: BigInt(0) })
            setSellAmountString('') // Reset sell amount string
            resetStopwatch()

            // Invalidate balances to refresh after successful swap
            invalidateBalances()
        }

        // Reset states, close modal, etc.
        setWaitingForConnectedWalletInteraction(false)
        setIsReviewSwapModalOpen(false)
        refreshStatus()
    }

    useEffect(() => {
        // Re-sanitize sell string in case of token change, but only if we have a value
        if (sellData.value !== BigInt(0)) {
            setSellAmountString(
                formatNumberStringWithCommas(returnSanitizedSellAmount(sellAmountString, sellData.token.decimals)) ?? ''
            )
        }
    }, [sellData.token]) // Only run when token changes, not all sellData changes

    useEffect(() => {
        if (!sellData.token?.address || !sellData.token.native) {
            getAllowance(+sellData.chainId, sellData.token.address, mainWalletAddress || '', swapContract.address)
                .then(allowance => {
                    setSellAllowance(allowance)
                })
                .catch(e => {
                    logger.error(
                        `Cannot get allowance (chainId = ${+sellData.chainId}, token=${sellData.token.address},wallet=${mainWalletAddress || ''},swapContract=${swapContract.address})`,
                        e
                    )
                })
        }
    }, [sellData.token])

    const swapContract = useSwapContract(sellData, LiquiditySource.unavailable)

    const { onToggleExchangeTokens: onToggleExchangeTokensTokens } = useToggleExchangeTokens({
        isWrapOrUnwrapOnly: isWrapOnly(sellData, buyData) || isUnWrapOnly(sellData, buyData),
        isBuyOutputLoading,
        updateBothData,
        sellData,
        buyData,
        setSellAmountString,
    })

    // Remove the checkBalancesAndAllowances function and replace with this version
    const checkBalancesAndAllowances = async (
        tokenAddress: string,
        amount: bigint,
        isNative: boolean
    ): Promise<{ hasBalance: boolean; hasAllowance?: boolean }> => {
        try {
            let relevantBalance: bigint

            if (isNative) {
                relevantBalance = nativeBalance?.bigintValue || BigInt(0)
            } else if (tokenAddress === WRAPPED_GAS_TOKENS[sellData.chainId]?.address) {
                relevantBalance = wrappedNativeBalance?.bigintValue || BigInt(0)
            } else {
                relevantBalance = sellData.token.userBalance || BigInt(0)
            }

            if (relevantBalance < amount) {
                return { hasBalance: false }
            }

            // Only check allowance for non-native tokens
            if (!isNative) {
                const allowance = await getAllowance(
                    +sellData.chainId,
                    tokenAddress,
                    mainWalletAddress || '',
                    swapContract.address
                )

                return {
                    hasBalance: true,
                    hasAllowance: BigInt(allowance) >= amount,
                }
            }

            // Native tokens don't need allowance
            return {
                hasBalance: true,
                hasAllowance: true,
            }
        } catch (error) {
            logger.error('Error checking balances and allowances:', error)
            return {
                hasBalance: false,
                hasAllowance: false,
            }
        }
    }

    // Update onConfirmExchange to use the new function
    const onConfirmExchange = async () => {
        let initialStep = StepNames.UNSTARTED

        try {
            if (isWrapOnly(sellData, buyData)) {
                initialStep = StepNames.DEPOSIT_WETH
            } else if (isUnWrapOnly(sellData, buyData)) {
                initialStep = StepNames.UNWRAP_WETH
            } else {
                if (!sellData.token?.address || !sellData.value) {
                    initialStep = StepNames.UNSTARTED
                } else {
                    const isNativeToken = sellData.token.address === NATIVE_TOKEN_ADDRESS[sellData.chainId]
                    const wrappedToken = WRAPPED_GAS_TOKENS[sellData.chainId]

                    logger.info(
                        `[onConfirmExchange]: isNativeToken=${isNativeToken}, wrappedToken=${wrappedToken.address}, nativeBalance=`,
                        nativeBalance,
                        `wrappedNativeBalance=`,
                        wrappedNativeBalance
                    )

                    if (isNativeToken && wrappedToken) {
                        // Check native token balance first
                        const _nativeBalance = nativeBalance?.bigintValue || BigInt(0)

                        // TODO: Low priority: the max amount that can be spent in this case is ETH balance + WETH balance - gas fees in ETH.
                        // For example if I have 0.1 ETH and 0.3 WETH, I can spend 0.4 ETH (minus gas fees), but my 0.1 ETH
                        // would need to be wrapped first.
                        // So in case of native assets, we could only wrap the diff, since the solver spends WETH only
                        // If WETH balance > sellData.value, then the wrap step can be skipped.

                        // The maximum amount that can be spent is
                        if (_nativeBalance < sellData.value) {
                            initialStep = StepNames.UNSTARTED
                        } else {
                            // Check wrapped token balance and allowance
                            const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                                wrappedToken.address,
                                sellData.value,
                                false
                            )

                            logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                            if (!hasBalance) {
                                initialStep = StepNames.DEPOSIT_WETH
                            } else {
                                initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                            }
                        }
                    } else {
                        // Check if this is a Solana CCTP transaction
                        const isSolanaCCTP =
                            sellData.chainId === ChainId.Solana && liquiditySource === LiquiditySource.cctpDirect

                        // Skip allowance check for Solana CCTP transactions
                        if (isSolanaCCTP || liquiditySource === LiquiditySource.jupiter) {
                            logger.info('[onConfirmExchange]: Skipping allowance check for CCTP or Jupiter')
                            initialStep = StepNames.CONFIRM_SWAP
                        } else {
                            // For all other non-native tokens
                            const { hasBalance, hasAllowance } = await checkBalancesAndAllowances(
                                sellData.token.address,
                                sellData.value,
                                false
                            )

                            logger.info('[onConfirmExchange]: hasBalance=', hasBalance, 'hasAllowance=', hasAllowance)

                            if (!hasBalance) {
                                initialStep = StepNames.UNSTARTED
                            } else {
                                initialStep = hasAllowance ? StepNames.CONFIRM_SWAP : StepNames.APPROVE_TOKENS
                            }
                        }
                    }
                }
            }
        } catch (error) {
            logger.error('Error determining initial step:', error)
            initialStep = StepNames.UNSTARTED
        }

        const newExchangeId = uuidv4()

        logger.info(`[onConfirmExchange] Determined initial step:`, initialStep)

        // Ensure liquiditySource is set for both sellData and buyData
        updateHistory(newExchangeId, {
            buyData: {
                ...buyData,
                liquiditySource: liquiditySource || LiquiditySource.unavailable,
            },
            sellData: {
                ...sellData,
                liquiditySource: liquiditySource || LiquiditySource.unavailable,
            },
            currentStep: initialStep,
        })

        setExchangeId(newExchangeId)
        setCurrentModalId(newExchangeId)

        setIsReviewSwapModalOpen(true)

        // Track when trade is confirmed by user
        posthog.capture('trade_confirmed', {
            from_token: sellData.token.symbol,
            to_token: buyData.token.symbol,
            from_amount: sellData.value.toString(),
            expected_to_amount: buyData.value.toString(),
            user_address: mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
            chain: sellData.chainId,
            fees: feesDisplay,
            miles: miles,
            initial_step: initialStep,
            is_cross_chain: isCrossChain,
            liquidity_source: liquiditySource,
        })
    }

    const referrer = useMemo(() => searchParams.get('referrer') ?? undefined, [searchParams.toString()])

    // Add handler for copying buy amount
    const handleCopyBuyAmount = () => {
        const amount = formatExchangeAmountWithCommas(buyData)
        navigator.clipboard.writeText(amount)
        toast.success('Amount copied to clipboard!', {
            position: 'bottom-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        })
    }

    const exchangeTokenHandler = async (quote?: IQuoteData) => {
        try {
            // Use the exchange function from the component level hook
            await exchange(referrer, quote)
        } catch (error: any) {
            // Don't show error toast for user rejections
            if (
                error.message?.includes('User rejected') ||
                error.message?.includes('user rejected') ||
                error.code === 4001
            ) {
                return
            }

            // Check if AI was used to generate this quote
            const wasAIUsed = localStorage.getItem('ai_quote_generated') === 'true'

            // Check if custom recipient is being used
            const isCustomRecipientUsed = localStorage.getItem('custom_recipient_used') === 'true'

            analyticsService.trackEvent('onClickConfirmSwap_Fail', {
                wallet_address: evmWalletAddress,
                error: error.message,
                error_code: error.code,
                source_token: sellData.token?.symbol,
                source_chain: sellData.chainId,
                source_amount: sellData.value.toString(),
                destination_token: buyData.token?.symbol,
                destination_chain: buyData.chainId,
                destination_amount: buyData.value.toString(),
                ai_quote_used: wasAIUsed,
                custom_recipient_used: isCustomRecipientUsed,
                timestamp: new Date().toISOString(),
            })

            // Show toast for other errors
            const errorMessage = (error.shortMessage || error.message || 'Error occurred').split('\n')[0]

            toast.error(errorMessage, {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            })
        }
    }

    const onClickConfirmSwap = () => {
        exchangeTokenHandler(quoteResponse.quote ?? undefined)

        // Calculate USD equivalents
        const sourceAmountUSD = sellData.token?.price_current
            ? (Number(sellData.value) * sellData.token.price_current) / 10 ** (sellData.token?.decimals || 18)
            : undefined

        const destinationAmountUSD = buyData.token?.price_current
            ? (Number(buyData.value) * buyData.token.price_current) / 10 ** (buyData.token?.decimals || 18)
            : undefined

        // Check if AI was used to generate this quote
        const wasAIUsed = localStorage.getItem('ai_quote_generated') === 'true'

        // Check if custom recipient is being used
        const isCustomRecipientUsed = localStorage.getItem('custom_recipient_used') === 'true'

        // Get the destination address for analytics
        const destinationAddr = isCustomRecipientUsed ? destinationAddress : undefined

        analyticsService.trackEvent('onClickConfirmSwap', {
            wallet_address: evmWalletAddress,
            is_cctp: isCCTP,
            swap_type: isCCTP ? 'cctp' : liquiditySource === LiquiditySource.jupiter ? 'jupiter' : 'market_maker',
            source_token: sellData.token?.symbol,
            source_chain: sellData.chainId,
            source_amount: sellData.value.toString(),
            source_amount_usd: sourceAmountUSD,
            source_token_price: sellData.token?.price_current,
            destination_token: buyData.token?.symbol,
            destination_chain: buyData.chainId,
            destination_amount: buyData.value.toString(),
            destination_amount_usd: destinationAmountUSD,
            destination_token_price: buyData.token?.price_current,
            ai_quote_used: wasAIUsed,
            custom_recipient_used: isCustomRecipientUsed,
            destination_address: destinationAddr,
            timestamp: new Date().toISOString(),
        })
    }

    const toggleCustomWallet = () => {
        logger.debug('[toggleCustomWallet] isCustomRecipientWallet:', { isCustomRecipientWallet })

        // If turning OFF custom wallet, reset to auto-detected address
        // If turning it ON, keep the current address (in case user is toggling back to a previously entered address)
        // Track custom recipient toggle with a single event
        analyticsService.trackEvent('custom_recipient_toggled', {
            enabled: !isCustomRecipientWallet, // Current value is before toggle, so we need to invert it
            chain: buyData.chainId,
            chain_name: getChainNameFromChainId(buyData.chainId),
            timestamp: new Date().toISOString(),
        })

        if (isCustomRecipientWallet) {
            // If we're turning it OFF
            const autoDetectedAddress = getAutoDetectedAddress(buyData.chainId)
            setDestinationAddress(autoDetectedAddress as Address)

            // Clear the localStorage flag
            localStorage.removeItem('custom_recipient_used')
        } else {
            // Set localStorage flag to track that custom recipient is being used
            localStorage.setItem('custom_recipient_used', 'true')
        }

        // Toggle the custom wallet state
        setIsCustomRecipientWallet(prev => !prev)

        // Refetch quote after state update
        setTimeout(() => {
            refetchQuote()
        }, 0)
    }

    const { destinationAddress, setDestinationAddress } = useDestinationAddress({
        isCrossChain,
        buyData,
        sellData,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
    })

    // Define a callback for successful swaps that refreshes balances
    const onSwapSuccess = async () => {
        // Refresh all balances and wait for them to update
        await refreshAllBalancesAndWait()
    }

    // SNS domain resolution for Solana addresses
    const {
        resolvedAddress: resolvedSNSAddress,
        isResolving: isResolvingSNS,
        resolveAddress: resolveSNSAddress,
    } = useSNSResolver()

    // ENS domain resolution for EVM addresses
    // Use the new wagmi-based ENS resolver hook for the current destination address
    const [ensNameToResolve, setEnsNameToResolve] = useState<string | undefined>(destinationAddress as string)

    const { data: resolvedENSAddress, isLoading: isResolvingENS } = useEnsAddress({
        name: ensNameToResolve || '',
        chainId: 1, // Always resolve ENS on Ethereum mainnet
    })

    // Create a function to manually resolve ENS names
    const resolveENSAddress = useCallback((name: string) => {
        // Resolve ENS name
        setEnsNameToResolve(name)
    }, [])

    // State to track the original domain for display purposes
    const [originalSNSDomain, setOriginalSNSDomain] = useState<string | null>(null)
    const [originalENSDomain, setOriginalENSDomain] = useState<string | null>(null)

    // Get the exchange function from the hook
    const { exchange } = useExchangeToken(exchangeId, liquiditySource, destinationAddress, onSwapSuccess)

    const onChangeDestAddress = (event: ChangeEvent<HTMLInputElement>) => {
        const newAddress = event.target.value as Address

        // Handle destination address change

        // If this is a Solana chain and looks like an SNS domain, store it and try to resolve it
        if (buyData.chainId === ChainId.Solana && isSNSDomain(newAddress)) {
            // Detected SNS domain on Solana chain
            setOriginalSNSDomain(newAddress as string)
            setOriginalENSDomain(null) // Clear ENS domain if we're using SNS
            setDestinationAddress(newAddress)
            // Try to resolve the SNS domain
            resolveSNSAddress(newAddress)
        }
        // If this is an EVM chain and looks like an ENS domain, store it and try to resolve it
        else if (buyData.chainId !== ChainId.Solana && buyData.chainId !== ChainId.Tron && isENSDomain(newAddress)) {
            // Detected ENS domain on EVM chain
            setOriginalENSDomain(newAddress as string)
            setOriginalSNSDomain(null) // Clear SNS domain if we're using ENS
            setDestinationAddress(newAddress)
            // Try to resolve the ENS domain
            resolveENSAddress(newAddress)
        } else {
            // Regular address detected (not SNS/ENS)
            // For non-domain addresses, clear the original domains
            setOriginalSNSDomain(null)
            setOriginalENSDomain(null)
            setDestinationAddress(newAddress)
        }

        // If we have a resolved address but the user is editing it, clear the original domains
        // This ensures the badge is removed when the address doesn't match anymore
        if (
            (originalSNSDomain || originalENSDomain) &&
            destinationAddress &&
            newAddress !== originalSNSDomain &&
            newAddress !== originalENSDomain &&
            newAddress !== destinationAddress
        ) {
            setOriginalSNSDomain(null)
            setOriginalENSDomain(null)
        }

        // Track when a custom recipient address is entered
        if (isCustomRecipientWallet && newAddress) {
            analyticsService.trackEvent('custom_recipient_address_entered', {
                chain: buyData.chainId,
                chain_name: getChainNameFromChainId(buyData.chainId),
                address_length: newAddress.length,
                destination_address: newAddress,
                timestamp: new Date().toISOString(),
            })

            // Set localStorage flag to track that custom recipient is being used
            localStorage.setItem('custom_recipient_used', 'true')
        }
    }

    // When resolved addresses change, update the destination address if we have a domain
    useEffect(() => {
        // Handle resolved addresses updates

        // Handle SNS domain resolution
        if (
            buyData.chainId === ChainId.Solana &&
            originalSNSDomain &&
            resolvedSNSAddress &&
            isSNSDomain(originalSNSDomain)
        ) {
            // Set destination address to resolved SNS address
            // Update the destination address to the resolved address
            // This will be used for the actual transaction
            // When this happens, the ExchangeDataContext will automatically refresh the quote
            // with the resolved address
            setDestinationAddress(resolvedSNSAddress as Address)
        }

        // Handle ENS domain resolution
        // First case: we have an originalENSDomain and resolvedENSAddress
        if (
            buyData.chainId !== ChainId.Solana &&
            buyData.chainId !== ChainId.Tron &&
            originalENSDomain &&
            resolvedENSAddress &&
            isENSDomain(originalENSDomain)
        ) {
            // Set destination address to resolved ENS address
            setDestinationAddress(resolvedENSAddress as Address)
        }
        // Second case: we have a resolvedENSAddress but no originalENSDomain
        // This can happen if the ENS resolution happens but the originalENSDomain wasn't set
        else if (
            buyData.chainId !== ChainId.Solana &&
            buyData.chainId !== ChainId.Tron &&
            resolvedENSAddress &&
            !originalENSDomain
        ) {
            // If we have a resolved ENS address but no originalENSDomain,
            // we should still use the resolved address
            setDestinationAddress(resolvedENSAddress as Address)
        }
    }, [resolvedSNSAddress, resolvedENSAddress, originalSNSDomain, originalENSDomain, buyData.chainId])

    useEffect(() => {
        // Only update destination address if custom recipient is not enabled
        if (!isCustomRecipientWallet) {
            const detectedAddress = getAutoDetectedAddress(buyData.chainId)
            logger.debug('[ExchangePage] Setting default address for chain', {
                chainId: buyData.chainId,
                isCustomRecipientWallet,
                detectedAddress,
            })
            setDestinationAddress(detectedAddress as Address)
        } else {
            logger.debug('[ExchangePage] Keeping custom address', {
                isCustomRecipientWallet,
                destinationAddress,
            })
        }
    }, [buyData.chainId, isEVMConnected, isSolanaConnected, isTronConnected, isCustomRecipientWallet])

    // Add this wrapper function inside your component
    const updateBothDataWrapper = (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => {
        updateBothData(data.sell, data.buy)
    }

    // Function to update URL with current token data
    const updateUrlWithTokenData = useCallback(() => {
        // Skip if tokens aren't loaded yet
        if (!sellData.token || !buyData.token) return

        // Create a new URLSearchParams object based on the current one
        const newParams = new URLSearchParams(searchParams.toString())

        // Update the parameters with current token data
        newParams.set('sellChain', sellData.chainId.toString())
        newParams.set('sellToken', sellData.token.address)
        newParams.set('buyChain', buyData.chainId.toString())
        newParams.set('buyToken', buyData.token.address)

        // Only update if parameters have changed
        if (newParams.toString() !== searchParams.toString()) {
            // Update URL with current token data
            setSearchParams(newParams)
        }
    }, [sellData.token, sellData.chainId, buyData.token, buyData.chainId, searchParams, setSearchParams])

    // Use a ref to track if we've already processed the URL parameters
    const processedUrlParamsRef = useRef(false)
    // Use a ref to track if token selection is being driven by user interaction vs URL params
    const userInitiatedTokenSelectionRef = useRef(false)
    // Use a ref to track if tokens have been initialized from localStorage/URL
    const tokensInitializedRef = useRef(false)

    // Then use the wrapper in your effect - run once on mount to process URL parameters
    useEffect(() => {
        // Skip if we've already processed the URL parameters or tokens are still loading
        if (processedUrlParamsRef.current || tokensLoading) {
            return
        }

        // Check if URL already has token parameters
        const hasSellChain = searchParams.has('sellChain')
        const hasSellToken = searchParams.has('sellToken')
        const hasBuyChain = searchParams.has('buyChain')
        const hasBuyToken = searchParams.has('buyToken')

        // If URL already has parameters, validate them
        if (hasSellChain && hasSellToken && hasBuyChain && hasBuyToken) {
            // Found URL parameters, validate them
            validateUrlTokenParams(searchParams, updateBothDataWrapper, setSellAmountString)

            // Mark as processed to prevent reprocessing
            processedUrlParamsRef.current = true
            tokensInitializedRef.current = true
            logger.info('Processed URL parameters from repeat trade')
        } else if (!hasSellChain && !hasSellToken && !hasBuyChain && !hasBuyToken) {
            // Only update URL if it's completely empty of token parameters
            // No URL parameters found, update URL with current token data
            updateUrlWithTokenData()
            processedUrlParamsRef.current = true
            tokensInitializedRef.current = true
        }
    }, [searchParams, updateBothDataWrapper, updateUrlWithTokenData, setSearchParams, tokensLoading]) // Added tokensLoading dependency

    // Also add runtime validation whenever tokens change
    useEffect(() => {
        // Skip if tokens aren't loaded yet
        if (!sellData.token || !buyData.token) return

        // Mark tokens as initialized once we have valid tokens
        if (!tokensInitializedRef.current) {
            tokensInitializedRef.current = true
            logger.info('Tokens initialized from context')
        }

        // Check for same token on same chain
        if (isSameExchangeToken(sellData, buyData)) {
            // Allow same token if it's a native token (for wrap/unwrap operations)
            const isNativeToken = sellData.token?.address === NULLADDRESS || sellData.token?.native === true

            if (!isNativeToken) {
                logger.info('Detected same token on both sides - applying validated defaults')

                // Get validated default tokens that won't create duplicates
                const { sellToken, buyToken } = validateDefaultTokens(sellData, buyData)

                // If only the sell token needs to be reset
                if (buyToken === buyData.token) {
                    updateSellData({
                        token: sellToken,
                        chainId: sellToken.chainId,
                    })
                }
                // If both tokens need to be reset
                else {
                    updateBothData(
                        {
                            token: sellToken,
                            chainId: sellToken.chainId,
                        },
                        {
                            token: buyToken,
                            chainId: buyToken.chainId,
                        }
                    )
                }
            } else {
                logger.info('Native token detected on both sides - allowing for wrap/unwrap operations')
                // Update URL with current token data when tokens change
                updateUrlWithTokenData()
            }
        } else {
            // Check if this token change is user-initiated vs. URL-driven
            // Only update URL if the change was user-initiated or we're not processing URL parameters
            const hasSellChain = searchParams.has('sellChain')
            const hasSellToken = searchParams.has('sellToken')
            const hasBuyChain = searchParams.has('buyChain')
            const hasBuyToken = searchParams.has('buyToken')

            // If user initiated the token change or we're not processing URL parameters
            if (
                userInitiatedTokenSelectionRef.current ||
                !(hasSellChain && hasSellToken && hasBuyChain && hasBuyToken)
            ) {
                // Update URL with current token data when tokens change
                // This ensures URL always reflects what's shown in the UI
                updateUrlWithTokenData()

                // Reset the user initiated flag after we've processed it
                if (userInitiatedTokenSelectionRef.current) {
                    userInitiatedTokenSelectionRef.current = false
                }
            }
        }
    }, [
        sellData.token?.address,
        buyData.token?.address,
        sellData.chainId,
        buyData.chainId,
        updateUrlWithTokenData,
        searchParams,
    ])

    // Update your token selection handler to use the helper
    const onTokenSelectionChange = (newData: { token?: IToken; chainId?: number }, type: 'buy' | 'sell') => {
        // Skip if tokens haven't been fully initialized yet
        if (!tokensInitializedRef.current) {
            logger.warn('Skipping token selection as tokens are not fully initialized yet')
            return
        }

        // Mark that this token selection is user-initiated
        userInitiatedTokenSelectionRef.current = true

        logger.info('User initiated token selection', {
            type,
            newToken: newData.token?.symbol,
            newChainId: newData.chainId,
        })

        // Handle the token selection
        handleTokenSelection(newData, type, sellData, buyData, updateBothDataWrapper)

        // Refetch quote after token selection to ensure we have an updated quote
        // Use setTimeout to ensure state updates have completed
        setTimeout(() => {
            refetchQuote()
            // Update URL params after token selection changes
            updateUrlWithTokenData()
        }, 0)
    }

    // Update the cleanup effect
    useEffect(() => {
        const abortController = new AbortController()

        return () => {
            abortController.abort()
        }
    }, [])

    // Refetch quote when token selection changes
    useEffect(() => {
        // Skip if we're in the middle of a swap operation
        if (isToggleInProgress) {
            return
        }

        // Skip for wrap/unwrap only
        if (isWrapOrUnwrapOnly) {
            return
        }

        if (sellData.token?.address && buyData.token?.address && sellData.value !== BigInt(0)) {
            // Format amounts for analytics tracking
            const formatBigIntToReadable = (amount: bigint, decimals: number = 6): string => {
                if (amount === BigInt(0)) return '0'
                const amountStr = amount.toString()

                if (amountStr.length <= decimals) {
                    // Handle values less than 1
                    const leadingZeros = decimals - amountStr.length
                    return `0.${'0'.repeat(leadingZeros)}${amountStr}`.replace(/0+$/, '')
                }

                // Normal case: Insert decimal point
                const integerPart = amountStr.slice(0, amountStr.length - decimals) || '0'
                const decimalPart = amountStr.slice(amountStr.length - decimals)
                const trimmedDecimalPart = decimalPart.replace(/0+$/, '')

                return trimmedDecimalPart ? `${integerPart}.${trimmedDecimalPart}` : integerPart
            }

            // Format amounts as readable values
            const fromAmount = formatBigIntToReadable(sellData.value, sellData.token.decimals)
            const expectedToAmount = formatBigIntToReadable(buyData.value, buyData.token.decimals)

            // Track when user starts the trade flow
            posthog.capture('trade_initiated', {
                from_token: sellData.token.symbol,
                to_token: buyData.token.symbol,
                from_amount: fromAmount,
                from_amount_raw: sellData.value.toString(),
                expected_to_amount: expectedToAmount,
                expected_to_amount_raw: buyData.value.toString(),
                user_address:
                    mainWalletAddress || evmWalletAddress || solanaWalletAddress?.toString() || tronWalletAddress,
                chain_id: sellData.chainId,
                chain: getChainNameFromChainId(Number(sellData.chainId)),
            })

            // Use window.setTimeout to ensure proper typing
            window.setTimeout(() => {
                refetchQuote()
            }, 0)
        }
    }, [
        `${sellData.token?.address}-${sellData.token?.chainId}-${buyData.token?.address}-${buyData.token?.chainId}-${sellData.value}`,
        isToggleInProgress, // Add isToggleInProgress to dependencies
    ])

    // Feather order state
    const [isFeatherOrderModalOpen, setIsFeatherOrderModalOpen] = useState(false)

    // Track modal state changes in the OrderUpdates store for background notifications
    const setModalOpen = useOrderUpdatesStore(state => state.setModalOpen)

    useEffect(() => {
        if (exchangeId && orderId) {
            const isModalOpen = isReviewSwapModalOpen
            logger.info(
                `[bg-tx] 🔄 Setting modal state for ${exchangeId}:${orderId} to ${isModalOpen ? 'OPEN' : 'CLOSED'}`
            )
            setModalOpen(exchangeId, orderId, isModalOpen)
        }
    }, [exchangeId, orderId, isReviewSwapModalOpen, setModalOpen])

    const [featherOrderStatus, _] = useState<IFeatherOrderStatusResponse | null>(null)

    // Get the chain name for the Feather notice
    const buyChainName = useMemo(() => {
        const chainNameKey = getChainNameFromChainId(buyData.chainId)
        return getChainName(chainNameKey)
    }, [buyData.chainId])

    // State for Feather quote error
    const [featherQuoteErrorState, setFeatherQuoteErrorState] = useState<FeatherQuoteError | null>(null)

    // Automatically enable Custom Wallet mode when Bitcoin is selected as buy chain
    // and set default Bitcoin address if none is set yet
    useEffect(() => {
        if (!isFeatherEnabled) {
            return
        }

        if (buyData.chainId === ChainId.Bitcoin) {
            // Enable custom wallet
            setIsCustomRecipientWallet(true)
        }
    }, [buyData.chainId, setIsCustomRecipientWallet, destinationAddress, setDestinationAddress])

    // Effect to fetch Feather quotes for BTC destination
    useEffect(() => {
        // Skip if feature is not enabled or not a Feather chain or if we already have a valid quote
        if (
            !isFeatherEnabled ||
            !isFeatherOrder ||
            isFeatherOrderModalOpen ||
            !destinationAddress ||
            sellData.value === BigInt(0)
        ) {
            setIsBuyOutputLoading(false)
            return
        }

        // Implement a small delay before starting to fetch quotes to avoid API calls during typing
        const typingDelay = 500 // 500ms delay

        // Skip checks for manual refresh - use isManuallyRefreshing state instead of refreshStatus
        const skipChecks = isManuallyRefreshing

        // Check if we already have a valid quote that hasn't expired yet
        if (!skipChecks && sellData.featherQuoteId && sellData.featherExpiresAt) {
            const now = Math.floor(Date.now() / 1000)
            const hasValidQuote = sellData.featherExpiresAt > now

            if (hasValidQuote) {
                logger.info('Using existing valid Feather quote', {
                    quoteId: sellData.featherQuoteId,
                    expiresAt: new Date(sellData.featherExpiresAt * 1000).toISOString(),
                    timeRemaining: `${sellData.featherExpiresAt - now} seconds`,
                })

                // We already have a valid quote, no need to fetch again
                setIsBuyOutputLoading(false)
                return
            } else {
                logger.info('Feather quote expired, requesting new quote', {
                    expiredQuoteId: sellData.featherQuoteId,
                    expiredAt: new Date(sellData.featherExpiresAt * 1000).toISOString(),
                })
            }
        }

        // Don't continue if we've already encountered an error on the current input values
        // and we're not doing a manual refresh
        if (!skipChecks && featherQuoteErrorState) {
            setIsBuyOutputLoading(false)
            return
        }

        // Set loading state before starting the debounce
        setIsBuyOutputLoading(true)

        // Clear any existing errors when input changes
        if (featherQuoteErrorState) {
            setFeatherQuoteErrorState(null)
        }

        // Use a debounce timer to avoid too many API calls
        let debounceTimer: NodeJS.Timeout | null = null

        // Track if an API call has been attempted
        let hasAttemptedFetch = false

        const fetchFeatherQuote = async () => {
            // Don't make multiple fetch attempts if we've already tried
            if (hasAttemptedFetch) {
                return
            }

            hasAttemptedFetch = true
            let hasQuoteSuccess = false

            try {
                // Use the refetchQuote method from the unified hook via context
                const result = await refetchQuote()
                const quoteData = result?.data

                // If we got a quote error, store it and stop further fetching
                if (quoteData && 'error' in quoteData) {
                    logger.error('Error fetching Feather quote:', quoteData.error)
                    setFeatherQuoteErrorState({
                        message: quoteData.error || 'Unknown error',
                        type: quoteData.errorType || 'unknown',
                    })
                    return
                }

                // If we got a quote and it's a Feather quote (has a quoteId), update sellData with Feather-specific info
                if (quoteData && featherQuote && featherQuote.quoteId) {
                    // Create updated sellData with Feather quote info
                    const updatedSellData = {
                        ...sellData,
                        isFeatherOrder: true,
                        featherQuoteId: featherQuote.quoteId,
                        featherExpiresAt: featherQuote.expiresAt,
                    }

                    // Update the sell data
                    updateSellData(updatedSellData)

                    // Mark that we got a successful quote
                    hasQuoteSuccess = true

                    // Keep the loading state active for a moment to allow UI transition
                    // This allows the "Loading..." to appear and then transition to the amount
                    setTimeout(() => {
                        setIsBuyOutputLoading(false)
                    }, 100)

                    return
                }
            } catch (error) {
                // Error is handled in the refetchQuote function
                logger.error('Error in fetchFeatherQuote:', error)
            } finally {
                // Only clear loading state here if we didn't successfully get a quote
                // Otherwise, the setTimeout above will handle it
                if (!hasQuoteSuccess) {
                    setIsBuyOutputLoading(false)
                }
            }
        }

        // Clear any existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer)
        }

        // Set a new timer with our typing delay
        debounceTimer = setTimeout(fetchFeatherQuote, typingDelay)

        // Cleanup function to clear timer if component unmounts or dependencies change
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer)
            }
        }
    }, [
        isFeatherEnabled,
        isFeatherOrder,
        isFeatherOrderModalOpen,
        sellData,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        destinationAddress,
        sellData.value,
        featherQuoteErrorState,
        refetchQuote,
        updateSellData,
        isManuallyRefreshing,
    ])

    // Update Feather quote error state when the error from the hook changes
    useEffect(() => {
        if (featherQuoteError !== featherQuoteErrorState?.message) {
            if (featherQuoteError) {
                setFeatherQuoteErrorState({
                    message: featherQuoteError,
                    type: 'unknown',
                })
            } else {
                setFeatherQuoteErrorState(null)
            }
        }
    }, [featherQuoteError, featherQuoteErrorState])

    // Clear Feather quote error when inputs change
    useEffect(() => {
        // Only clear error state when inputs actually change in a meaningful way
        if (
            sellData.value > BigInt(0) && // Only if we have a non-zero value
            featherQuoteErrorState !== null // And we actually have an error to clear
        ) {
            logger.debug('Clearing Feather quote error because inputs changed', {
                sellValue: sellData.value.toString(),
                sellToken: sellData.token?.symbol,
                buyToken: buyData.token?.symbol,
                destinationAddress,
            })
            setFeatherQuoteErrorState(null)
        }
    }, [sellData.value, sellData.token, buyData.token, destinationAddress])

    // Handler for creating a Feather order
    const onCreateFeatherOrder = async () => {
        if (!isFeatherEnabled) {
            logger.warn('Feather orders are disabled')
            return
        }

        // Add debugging
        console.log('onCreateFeatherOrder called', {
            isFeatherOrder: sellData.isFeatherOrder,
            featherQuoteId: sellData.featherQuoteId,
            destinationAddress,
            buyChainId: buyData.chainId,
        })

        if (!destinationAddress) {
            toast.error('Please enter a destination address')
            return
        }

        // Use the existing Feather quote/data
        if (!sellData.isFeatherOrder || !sellData.featherQuoteId) {
            logger.error('Cannot proceed: no feather quote data available')
            toast.error('No valid quote data found')
            return
        }

        // Simply open the modal with the existing quote data
        setIsFeatherOrderModalOpen(true)

        // Additional debugging after state update
        console.log('Setting isFeatherOrderModalOpen to true', {
            isFeatherOrderModalOpen: true,
            hasFeatherQuote: !!featherQuote,
            depositAddress: featherQuote?.srcDepositWallet,
        })
    }

    // Add a debug effect to monitor loading states
    useEffect(() => {
        logger.debug('Loading state debug:', {
            isBuyOutputLoading,
            isQuoteLoading,
            isFeatherOrder,
            hasSellValue: sellData.value > BigInt(0),
            hasDestinationAddress: !!destinationAddress,
            sellAmount: sellData.value.toString(),
            buyAmount: buyData.value.toString(),
            hasFeatherQuoteId: !!sellData.featherQuoteId,
        })
    }, [
        isBuyOutputLoading,
        isQuoteLoading,
        isFeatherOrder,
        sellData.value,
        destinationAddress,
        buyData.value,
        sellData.featherQuoteId,
    ])

    // Function to manually retry fetching a Feather quote after an error
    const retryFeatherQuote = useCallback(() => {
        // Skip if feature is disabled
        if (!isFeatherEnabled) {
            return
        }

        // Only retry if there's currently an error
        if (featherQuoteErrorState) {
            logger.info('Manually retrying Feather quote', {
                sellAmount: sellData.value.toString(),
                destinationAddress,
                errorType: featherQuoteErrorState.type,
            })

            // Clear the error state
            setFeatherQuoteErrorState(null)

            // Trigger a refetch immediately
            setTimeout(async () => {
                try {
                    setIsBuyOutputLoading(true)
                    const quoteData = await refetchQuote()

                    // Handle error if any
                    if (quoteData && 'error' in quoteData) {
                        logger.error('Error in retry attempt for Feather quote:', quoteData.error)
                        setFeatherQuoteErrorState({
                            message: quoteData.error || 'Unknown error',
                            type: quoteData.errorType || 'unknown',
                        })
                        return
                    }

                    // If we got a quote, update sellData with Feather-specific info
                    if (quoteData) {
                        const updatedSellData = {
                            ...sellData,
                            isFeatherOrder: true,
                            featherQuoteId: quoteData.quoteId,
                            featherExpiresAt: quoteData.expiresAt,
                        }

                        // Update the sell data
                        updateSellData(updatedSellData)
                    }
                } catch (error) {
                    logger.error('Error in retry for Feather quote:', error)
                } finally {
                    setIsBuyOutputLoading(false)
                }
            }, 0)
        }
    }, [featherQuoteErrorState, sellData, destinationAddress, refetchQuote, updateSellData, setIsBuyOutputLoading])

    // Modify handleManualRefresh to clear Feather errors and force a new quote fetch
    const handleManualRefreshWrapper = () => {
        // Handle Feather quotes differently
        if (isFeatherOrder) {
            logger.info('Manual refresh: refreshing Feather quote')

            // Clear error state if there was one
            if (featherQuoteErrorState) {
                setFeatherQuoteErrorState(null)
            }

            // Show loading state immediately
            setIsBuyOutputLoading(true)

            // Use the refreshQuote function which handles everything internally
            // including setting loading states, updating data and handling errors
            refetchQuote()
                .then((result: any) => {
                    // If we got a quote, update sellData with Feather-specific info
                    if (featherQuote && featherQuote.quoteId) {
                        const updatedSellData = {
                            ...sellData,
                            isFeatherOrder: true,
                            featherQuoteId: featherQuote.quoteId,
                            featherExpiresAt: featherQuote.expiresAt,
                        }

                        // Update the sell data
                        updateSellData(updatedSellData)
                    } else if (result.data?.error) {
                        // Handle error specifically for UI display
                        const errorMessage = result.data.error || 'Failed to refresh quote'
                        logger.error('Error in manual refresh for Feather quote:', errorMessage)
                        setFeatherQuoteErrorState({
                            message: errorMessage,
                            type: (result.data.errorType as string) || 'unknown',
                        })
                    }
                })
                .catch((error: Error) => {
                    // Handle any unexpected errors
                    logger.error('Unexpected error in manual refresh for Feather quote:', error)
                })
                .finally(() => {
                    // Ensure loading state is cleared when done
                    setTimeout(() => {
                        setIsBuyOutputLoading(false)
                    }, 100) // Small delay to allow UI to update smoothly
                })

            return
        }

        // Call the original refresh handler for non-Feather trades
        handleManualRefresh()
    }

    // Effect to monitor Feather order modal state and debug if needed
    useEffect(() => {
        if (isFeatherEnabled && isFeatherOrderModalOpen) {
            logger.info('Feather order modal is open with data:', {
                isFeatherOrder: sellData.isFeatherOrder,
                featherQuoteId: sellData.featherQuoteId,
                featherExpiresAt: sellData.featherExpiresAt,
                buyChain: buyData.chainId,
                depositAddress: featherQuote?.srcDepositWallet,
            })
        }
    }, [
        isFeatherOrderModalOpen,
        sellData.isFeatherOrder,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        buyData.chainId,
        featherQuote,
    ])

    // Effect to monitor feather quotes and state
    useEffect(() => {
        if (isFeatherEnabled && isFeatherOrder && sellData.value > BigInt(0)) {
            logger.debug('Feather order status update:', {
                isFeatherOrder,
                hasFeatherQuote: !!featherQuote,
                hasSellDataQuoteId: !!sellData.featherQuoteId,
                featherQuoteExpiry: sellData.featherExpiresAt,
                depositAddress: featherQuote?.srcDepositWallet,
                buyChainId: buyData.chainId,
                isModalOpen: isFeatherOrderModalOpen,
            })
        }
    }, [
        isFeatherOrder,
        sellData.featherQuoteId,
        sellData.featherExpiresAt,
        featherQuote,
        sellData.value,
        buyData.chainId,
        isFeatherOrderModalOpen,
    ])

    // Debug effect to trace the buy amount string generation for Feather orders
    useEffect(() => {
        if (isFeatherOrder && buyData.value > BigInt(0)) {
            const formattedAmount = formatExchangeAmountWithCommas(buyData)
            logger.info('Feather order buy amount display:', {
                rawValue: buyData.value.toString(),
                formattedValue: formattedAmount,
                isBuyOutputLoading,
            })
        }
    }, [isFeatherOrder, buyData.value, isBuyOutputLoading])

    // Add a useEffect to force UI updates for Feather orders when buyData changes
    useEffect(() => {
        if (isFeatherOrder && buyData.value > BigInt(0) && !isBuyOutputLoading) {
            // Force a refresh of the UI when buyData.value changes for Feather orders
            logger.info('Feather order buy amount updated:', {
                buyAmount: buyData.value.toString(),
                formattedAmount: formatExchangeAmountWithCommas(buyData),
            })
        }
    }, [isFeatherOrder, buyData.value, isBuyOutputLoading])

    // Helper function to determine if gas fee panel should be shown
    const shouldShowGasFeePanel = () => {
        // Don't show for incomplete decimal inputs
        if (sellAmountString === '0.' || sellAmountString === '.') {
            return false
        }

        // Don't show if output value is zero
        if (buyData.value === BigInt(0)) {
            return false
        }

        return true
    }

    return (
        <>
            <div className='flex w-full flex-col items-center'>
                {!hideAIInput && <AIInputBox className='mb-4 w-[520px] max-w-[calc(100%-24px)]' setMax={setMax} />}
                <div className='exchange-container relative mx-auto mb-10 w-[520px] max-w-[calc(100%-24px)] space-y-2 rounded-xl bg-mach-darkBlue transition-all duration-500 ease-in-out will-change-auto mobile-custom:px-4 mobile-custom:py-4'>
                    {/* Display Feather Order Notice below the Swap title */}
                    {isFeatherEnabled && isFeatherOrder && (
                        <div className='mb-2 px-4'>
                            <FeatherOrderNotice chainName={buyChainName} />
                        </div>
                    )}

                    <div className='space-y-2'>
                        <div className='relative z-10 space-y-[10px]'>
                            <FromSection
                                sellData={sellData}
                                buyData={buyData}
                                isCorrectWalletConnected={isCorrectWalletConnected}
                                isInsufficientBalance={isInsufficientBalance}
                                quoteResponse={quoteResponse}
                                sellAmountString={sellAmountString}
                                isGasEstimationFetched={isGasEstimationFetched}
                                onChangeSellValue={onChangeSellValue}
                                onSetMax={setMax}
                                onTokenSelectionChange={onTokenSelectionChange}
                            />

                            <div className='toggle-button-container'>
                                <ToggleExchangeTokens onToggleExchangeTokens={onToggleExchangeTokensTokens} />
                            </div>

                            <ToSection
                                buyData={buyData}
                                sellData={sellData}
                                isCustomRecipientWallet={isCustomRecipientWallet}
                                isCrossChain={isCrossChain}
                                onToggleCustomWallet={toggleCustomWallet}
                                buyAmountString={formatExchangeAmountWithCommas(buyData)}
                                isBuyOutputLoading={isBuyOutputLoading}
                                isQuoteLoading={isQuoteLoading}
                                isCheckingLiquidity={isCheckingLiquidity}
                                liquidityAvailable={liquidityAvailable}
                                isManuallyRefreshing={isManuallyRefreshing}
                                isQuoteEnabled={isQuoteEnabled}
                                isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                                onTokenSelectionChange={onTokenSelectionChange}
                                onClickAmount={handleCopyBuyAmount}
                                destinationAddress={destinationAddress}
                                validateAddress={validateAddress}
                                onChangeDestAddress={onChangeDestAddress}
                                handleManualRefreshWrapper={handleManualRefreshWrapper}
                                isResolvingSNS={isResolvingSNS}
                                resolvedSNSAddress={resolvedSNSAddress}
                                isResolvingENS={isResolvingENS}
                                resolvedENSAddress={resolvedENSAddress}
                                miles={miles}
                                sandDollars={(() => {
                                    if (!hasErrors && !isFeatureEnabled('USE_TESTNETS_ONLY'))
                                        return quoteResponse?.quote?.sand_dollars
                                    else return undefined
                                })()}
                                hasErrors={hasErrors}
                            />
                        </div>

                        <div className='transition-all duration-500 ease-in-out will-change-auto'>
                            {shouldShowGasFeePanel() && (
                                <GasFeePanel
                                    isGasFeeExpanded={isGasFeeExpanded}
                                    gasFeeString={gasFeeString}
                                    onToggleGasFee={() => setIsGasFeeExpanded(!isGasFeeExpanded)}
                                    detailedRowsProps={{
                                        destinationAddress,
                                        evmWalletAddress,
                                        sellData,
                                        buyData,
                                        gasFeeString,
                                        liquiditySource,
                                        machFee,
                                        miles,
                                        sandDollars: quoteResponse?.quote?.sand_dollars
                                            ? createSandDollarsBalance(quoteResponse.quote.sand_dollars)?.toNumber()
                                            : undefined,
                                    }}
                                    isPriceFlipped={isPriceFlipped}
                                    setIsPriceFlipped={setIsPriceFlipped}
                                    sellData={sellData}
                                    buyData={buyData}
                                    isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                                    isBuyOutputLoading={isBuyOutputLoading}
                                    isQuoteLoading={isQuoteLoading}
                                />
                            )}
                        </div>

                        {/* Exchange page swap button logic. */}
                        <div className='transition-all duration-500 ease-in-out will-change-auto'>
                            {(() => {
                                let elements: JSX.Element[] = []

                                if (!isCorrectWalletConnected) {
                                    elements = [
                                        <div className='space-y-2' key='connect-button'>
                                            <ConnectWallet className='h-14' requiredChainType={sellData.chainId} />
                                        </div>,
                                    ]
                                } else {
                                    elements.push(
                                        <SwapActionButton
                                            key={`feather-swap-${sellData.value.toString()}-${buyData.value.toString()}-${sellData.featherQuoteId || ''}`}
                                            sellData={sellData}
                                            buyData={buyData}
                                            sellAmountString={sellAmountString}
                                            quoteResponse={quoteResponse}
                                            errors={{
                                                isInsufficientBalance,
                                                isQuoteInvalid,
                                            }}
                                            isBuyOutputLoading={isBuyOutputLoading}
                                            hasInsufficientGas={hasInsufficientGas}
                                            isCustomRecipientWallet={
                                                isCustomRecipientWallet || buyData.chainId === ChainId.Bitcoin
                                            }
                                            destinationAddress={destinationAddress}
                                            onConfirmExchange={onConfirmExchange}
                                            onCreateFeatherOrder={onCreateFeatherOrder}
                                            onRetryFeatherQuote={retryFeatherQuote}
                                            isGasEstimationFetched={isGasEstimationFetched}
                                            isQuoteLoading={isQuoteLoading}
                                            featherQuoteError={featherQuoteErrorState}
                                            isLiquidityUnavailable={!liquidityAvailable}
                                            isCheckingLiquidity={isCheckingLiquidity}
                                            liquidityError={liquidityError}
                                            liquiditySource={liquiditySource}
                                        />
                                    )

                                    // Add token sparklines for Solana tokens
                                    // Show sparklines whenever both tokens are selected on Solana chain, regardless of quote
                                    if (
                                        sellData.chainId === ChainId.Solana &&
                                        buyData.chainId === ChainId.Solana &&
                                        sellData.token.address && // Token is selected
                                        buyData.token.address // Token is selected
                                    ) {
                                        elements.push(
                                            <div
                                                key='token-sparklines'
                                                className='mt-4 flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-4'
                                            >
                                                <div className='flex-1 rounded-md bg-mach-gray p-2'>
                                                    <div className='mb-2 flex items-center gap-2'>
                                                        <img
                                                            src={sellData.token.logoURI}
                                                            alt={sellData.token.symbol}
                                                            className='h-5 w-5 rounded-full'
                                                        />
                                                        <span className='text-sm text-white'>
                                                            {sellData.token.symbol}
                                                        </span>
                                                    </div>
                                                    <TokenSparkline tokenMint={sellData.token.address} />
                                                </div>
                                                <div className='flex-1 rounded-md bg-mach-gray p-2'>
                                                    <div className='mb-2 flex items-center gap-2'>
                                                        <img
                                                            src={buyData.token.logoURI}
                                                            alt={buyData.token.symbol}
                                                            className='h-5 w-5 rounded-full'
                                                        />
                                                        <span className='text-sm text-white'>
                                                            {buyData.token.symbol}
                                                        </span>
                                                    </div>
                                                    <TokenSparkline tokenMint={buyData.token.address} />
                                                </div>
                                            </div>
                                        )
                                    }
                                }

                                return elements
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Feather Order Flow Modal */}
            {isFeatherEnabled && sellData.isFeatherOrder && sellData.featherQuoteId && (
                <FeatherFlow
                    isOpen={isFeatherOrderModalOpen}
                    onClose={() => {
                        console.log('Closing Feather order modal')
                        setIsFeatherOrderModalOpen(false)
                    }}
                    sellData={sellData}
                    buyData={buyData}
                    fees={[
                        {
                            label: 'Network Fee',
                            value: 0,
                            display: '$0.00',
                        },
                        {
                            label: 'Liquidity Provider Fee',
                            value: 0,
                            display: '$0.00',
                        },
                    ]}
                    depositAddress={(featherQuote?.srcDepositWallet as Address) || ('' as Address)}
                    miles={miles}
                />
            )}

            {/* Feather Order Status Modal */}
            {isFeatherEnabled && featherOrderStatus && (
                <FeatherOrderStatusModal
                    isOpen={!!featherOrderStatus && isFeatherOrderModalOpen}
                    onClose={() => {
                        console.log('Closing Feather order modal')
                        setIsFeatherOrderModalOpen(false)
                    }}
                    sellData={sellData}
                    buyData={buyData}
                    src_deposit_tx={featherOrderStatus?.src_deposit_tx}
                    dst_fill_tx={featherOrderStatus?.dst_fill_tx}
                    status={featherOrderStatus ? mapBackendToFrontendStatus(featherOrderStatus.status) : undefined}
                    orderId={featherOrderStatus?.orderId}
                />
            )}

            {transactionData && currentModalId && (!isFeatherOrder || !isFeatherEnabled) && (
                <SwapInProgressFlow
                    isOpen={isReviewSwapModalOpen}
                    onClose={onCloseReviewSwapModal}
                    onConfirm={onClickConfirmSwap}
                    fees={feesDisplay}
                    miles={miles}
                    sandDollars={quoteResponse?.quote?.sand_dollars}
                    destinationAddress={destinationAddress || mainWalletAddress}
                    liquiditySource={liquiditySource}
                    isGasEstimationFetched={isGasEstimationFetched}
                    {...transactionData}
                />
            )}

            {/* Jupiter Route Details Modal */}
            <JupiterRouteModal isOpen={isJupiterRouteModalOpen} onClose={closeJupiterRouteModal} />

            {/* Modal state tracking through TransactionNotifications component */}
            <TransactionNotifications />

            <AllTooltips />
        </>
    )
}

export default ExchangePage
